// Pure maths for the resistance-bundle check on the combined Volume screen (the "Hybrid sheet" of 2026.10.5_Bundling_Offer_V4.xlsx).
// Works for ANY bundle in pricing2Dependants (Silvalis today): nothing here knows a product. The user orders every product as a
// normal line; this file checks whether the partner products are ordered in large enough volumes for the resistance price.
// It only decides the PRICE. It never blocks an order.
import { autoMix, bundlePrice, groupProducts } from "../ResistanceVolume/calcBundle.js";

export const HYBRID_SHORTFALL = 0.02; // Hybrid sheet C5 "Shortfall allowance"

const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? 0 : Number(v));
const roundUp = (x) => Math.max(0, Math.ceil(x - 1e-9)); // 5.0000000001 must not become 6; never -0

/** productId -> litres/kg ordered, over all the product's lines (any pack size). */
export function orderedByProduct(rows, lines) {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const out = new Map();
  (lines || []).forEach((l) => {
    const row = byId.get(l.rowId);
    if (!row) return;
    out.set(row.product_id, (out.get(row.product_id) || 0) + num(l.boxes) * num(row.full_pack_volume));
  });
  return out;
}

export const choiceKey = (mainProductId, groupIndex) => `${mainProductId}:${groupIndex}`;

/**
 * @param rows       pricing2 rows for the crop
 * @param lines      [{ rowId, boxes }]
 * @param bundles    GET /api/resistance_bundles/{crop}  ([{ main, groups:[{ minCover, products:[...] }] }])
 * @param shortfall  0.02 = a partner may be 2% short. null = use each group's min_cover from the database (1 - min_cover), the same rule the order pricing uses
 * @param choices    { "<mainProductId>:<groupIndex>": productId }  the user's pick when a group has two products (Sorba or Denim Fit)
 * @returns one entry per resistance product of the crop, also when it is not ordered yet (hasMain false, all minimums 0),
 *          so the screen can always show the partner rows as the sheet does. Empty list when the server has no bundles.
 */
export function bundleCheck(rows, lines, bundles, shortfall = HYBRID_SHORTFALL, choices = {}) {
  const ordered = orderedByProduct(rows, lines);
  const names = new Map(groupProducts(rows).map((g) => [g.productId, g.name]));
  const results = [];

  (Array.isArray(bundles) ? bundles : []).forEach((b) => {
    if (!b || !b.main || !names.has(b.main.productId)) return;
    const mainLitres = ordered.get(b.main.productId) || 0;
    const hasMain = mainLitres > 0;
    const mainPerHa = num(b.main.dosePerSpray) * num(b.main.spraysPerSeason);
    const hectaresCovered = mainPerHa > 0 ? mainLitres / mainPerHa : 0;

    const partners = [];
    (b.groups || []).forEach((g, groupIndex) => {
      const options = (g.products || []).filter((p) => names.has(p.productId));
      if (options.length === 0) return;
      const picked = choices[choiceKey(b.main.productId, groupIndex)];
      const best = options.reduce((a, p) => ((ordered.get(p.productId) || 0) > (ordered.get(a.productId) || 0) ? p : a), options[0]);
      const chosen = options.find((p) => p.productId === picked) || best;
      const need = hectaresCovered * num(chosen.dosePerSpray) * num(chosen.spraysPerSeason);
      // min_cover empty = 1 (no shortfall), as on the server
      const sf = shortfall === null ? Math.round((1 - (num(g.minCover) > 0 ? num(g.minCover) : 1)) * 1e6) / 1e6 : shortfall;
      const needed = roundUp(need * (1 - sf));
      const have = ordered.get(chosen.productId) || 0;
      partners.push({
        key: choiceKey(b.main.productId, groupIndex),
        productId: chosen.productId,
        name: names.get(chosen.productId),
        dose: num(chosen.dosePerSpray), // per spray
        sprays: num(chosen.spraysPerSeason), // sprays per season
        options: options.map((p) => ({ productId: p.productId, name: names.get(p.productId) })),
        need, // before the shortfall allowance
        shortfall: sf, // the allowed shortfall that was applied (0.03 = 3%)
        needed, // the minimum to order
        ordered: have,
        shortBy: Math.max(0, needed - have),
        ok: have + 1e-9 >= needed,
      });
    });

    const missing = partners.filter((p) => !p.ok).map((p) => p.name);
    results.push({
      mainProductId: b.main.productId,
      mainName: names.get(b.main.productId),
      mainDose: num(b.main.dosePerSpray), // per spray
      mainSprays: num(b.main.spraysPerSeason), // sprays per season
      mainLitres,
      hectaresCovered,
      partners,
      hasMain,
      pass: hasMain && partners.every((p) => p.ok),
      message: !hasMain ? `Geen ${names.get(b.main.productId)}` : missing.length ? `${missing.join(", ")} te min` : "Reg",
    });
  });
  return results;
}

/** Map(pricing2.id -> resistance unit price | null) for calcVolumes: price when the check passes, null (= list price) when it does not.
 *  Every main product of a bundle is in the map, so it never gets a MyPack tier discount. */
export function resistancePrices(rows, checks) {
  const map = new Map();
  (checks || []).forEach((c) => {
    rows.filter((r) => r.product_id === c.mainProductId).forEach((r) => map.set(r.id, c.pass ? bundlePrice(r) : null));
  });
  return map;
}

/** Boxes that cover a partner's need: [{ rowId, boxes }], biggest pack first (the "Outo" mix when the product has two packs). */
export function partnerBoxes(rows, productId, need, shortfall = HYBRID_SHORTFALL) {
  const group = groupProducts(rows).find((g) => g.productId === productId);
  if (!group || !(need > 0)) return [];
  const big = group.packs[0];
  const small = group.packs.length > 1 ? group.packs[group.packs.length - 1] : null;
  if (small) {
    const mix = autoMix(need, shortfall, num(big.full_pack_volume), num(small.full_pack_volume));
    return [
      { rowId: big.id, boxes: mix.big },
      { rowId: small.id, boxes: mix.small },
    ].filter((m) => m.boxes > 0);
  }
  return [{ rowId: big.id, boxes: roundUp((need * (1 - shortfall)) / num(big.full_pack_volume)) }];
}
